/**
 * Scene item / preset / package mutation surface (WP-1).
 *
 * Defines the **TypeScript surface** described in
 * `app-platform-gtwin/docs/proposals/SCENE_ITEMS_SDK_SPEC.md` so consumer
 * code in the viewer compiles **today**. The real platform implementation
 * lands in Track A; until it does, the viewer routes through the
 * in-memory mock adapter behind the `VITE_GTWIN_SCENE_ITEMS` feature
 * flag (default `mock`).
 */

import type {
  ArtifactKind,
  ArtifactSceneContract,
} from "./artifactContract.js";
import { ARTIFACT_SCENE_CONTRACT } from "./artifactContract.js";
import type { SceneItem, SceneSlot } from "./types.js";
import { InvalidSlotError } from "./errors.js";

export type IntelligenceLevel = "L0" | "L1" | "L2" | "L3" | "L4";

export type ReadinessState =
  | "visualization-ready"
  | "asset-ready"
  | "simulation-ready"
  | "operation-ready";

export type ProvenanceSource =
  | "source-file"
  | "source-metadata"
  | "profiler"
  | "ai"
  | "human"
  | "converter"
  | "user-input"
  | string;

export type ProvenanceReviewStatus =
  | "unreviewed"
  | "candidate"
  | "approved"
  | "rejected"
  | "superseded"
  | "not-required";

export interface Provenance {
  source: ProvenanceSource;
  extractionMethod: string;
  confidence: number;
  trusted: boolean;
  reviewStatus: ProvenanceReviewStatus;
}

export interface EndpointRef {
  href: string;
  mediaType?: string;
}

export interface SceneItemInput {
  id: string;
  artifactKind: ArtifactKind;
  layerKind: string;
  slot: SceneSlot;
  title?: string;
  endpoints: Record<string, EndpointRef>;
  manifestRef?: { href: string; schema: string };
  sourceCrs?: string;
  bbox?: [number, number, number, number];
  timeExtent?: { start: string; end?: string } | null;
  verticalExtent?: { min: number; max: number } | null;
  indexNodeIds?: ReadonlyArray<string>;
  readiness?: ReadonlyArray<ReadinessState>;
  intelligenceLevel?: IntelligenceLevel;
  provenance: Provenance;
  extensions?: Record<string, unknown>;
}

export interface SceneItemPatch {
  id: string;
  visible?: boolean;
  opacity?: number;
  style?: Record<string, unknown>;
  title?: string;
  readiness?: ReadonlyArray<ReadinessState>;
  intelligenceLevel?: IntelligenceLevel;
  provenance?: Partial<Provenance>;
  extensions?: Record<string, unknown>;
}

export interface ScenePresetCamera {
  lonLat: [number, number];
  zoom?: number;
  height?: number;
  bearing?: number;
  pitch?: number;
}

export interface ScenePresetInput {
  id: string;
  label: string;
  show: ReadonlyArray<SceneSlot>;
  layerVisibility?: Readonly<Record<string, boolean>>;
  layerOpacity?: Readonly<Record<string, number>>;
  camera?: ScenePresetCamera;
  time?: { instant?: string; range?: { start: string; end: string } };
  description?: string;
}

export interface SceneItemValidationError {
  field: string;
  message: string;
}

export function validateSceneItem(
  input: SceneItemInput,
): SceneItemValidationError[] {
  const errors: SceneItemValidationError[] = [];

  if (!input.id || input.id.trim().length === 0) {
    errors.push({ field: "id", message: "id is required" });
  }
  const contract = ARTIFACT_SCENE_CONTRACT[input.artifactKind] as
    | ArtifactSceneContract
    | undefined;
  if (!contract) {
    errors.push({
      field: "artifactKind",
      message: `Unknown artifactKind "${input.artifactKind}". Allowed kinds: ${Object.keys(
        ARTIFACT_SCENE_CONTRACT,
      ).join(", ")}.`,
    });
    return errors;
  }
  if (input.slot !== contract.slot) {
    errors.push({
      field: "slot",
      message: `Slot "${input.slot}" does not match the canonical slot "${contract.slot}" for artifactKind "${input.artifactKind}".`,
    });
  }
  if (input.layerKind !== contract.layerKind) {
    errors.push({
      field: "layerKind",
      message: `layerKind "${input.layerKind}" does not match the canonical layerKind "${contract.layerKind}" for artifactKind "${input.artifactKind}".`,
    });
  }
  const expectedKey = contract.endpointKey;
  const aliasKeys = contract.endpointAliases ?? [];
  const acceptedKeys = [expectedKey, ...aliasKeys];
  const hasEndpoint = acceptedKeys.some((key) => {
    const value = input.endpoints[key];
    return Boolean(value && typeof value.href === "string" && value.href.length > 0);
  });
  if (!hasEndpoint) {
    errors.push({
      field: "endpoints",
      message: `Missing required endpoint key "${expectedKey}"${
        aliasKeys.length > 0 ? ` (or alias: ${aliasKeys.join(", ")})` : ""
      } for artifactKind "${input.artifactKind}".`,
    });
  }
  if (input.bbox !== undefined) {
    const valid = Array.isArray(input.bbox)
      && input.bbox.length === 4
      && input.bbox.every((value) => Number.isFinite(value));
    if (!valid) {
      errors.push({
        field: "bbox",
        message: "bbox must be [minX, minY, maxX, maxY] of finite numbers.",
      });
    } else {
      const [minX, minY, maxX, maxY] = input.bbox;
      if (minX > maxX || minY > maxY) {
        errors.push({
          field: "bbox",
          message: "bbox min must be <= max on both axes.",
        });
      }
    }
  }
  if (input.sourceCrs !== undefined) {
    const ok = /^EPSG:\d+$/i.test(input.sourceCrs)
      || input.sourceCrs.trim().toUpperCase() === "LOCAL";
    if (!ok) {
      errors.push({
        field: "sourceCrs",
        message: 'sourceCrs must be an EPSG code (e.g. "EPSG:4326") or "LOCAL".',
      });
    }
  }
  if (
    input.intelligenceLevel !== undefined
    && !["L0", "L1", "L2", "L3", "L4"].includes(input.intelligenceLevel)
  ) {
    errors.push({
      field: "intelligenceLevel",
      message: 'intelligenceLevel must be one of "L0".."L4".',
    });
  }
  if (!input.provenance) {
    errors.push({ field: "provenance", message: "provenance is required" });
  } else if (input.provenance.confidence < 0 || input.provenance.confidence > 1) {
    errors.push({
      field: "provenance.confidence",
      message: "provenance.confidence must be in [0, 1].",
    });
  }
  return errors;
}

export function throwIfInvalid(input: SceneItemInput): SceneItemInput {
  const errors = validateSceneItem(input);
  if (errors.length > 0) {
    const summary = errors
      .map((error) => `${error.field}: ${error.message}`)
      .join("; ");
    throw new InvalidSlotError(`invalid-scene-item: ${summary}`);
  }
  return input;
}

export interface SceneItemsCopyFromInput {
  sourceGtwinId: string;
  itemIds?: ReadonlyArray<string>;
  deleteFromSource?: boolean;
  updatedBy?: string;
}

export interface SceneItemsCopyFromResult {
  copied: number;
  items: ReadonlyArray<string>;
  targetVersion: number;
  deleteFromSource: boolean;
}

export interface SceneItemsAdapter {
  add(gtwinId: string, item: SceneItemInput): Promise<SceneItem>;
  remove(gtwinId: string, itemId: string): Promise<void>;
  patch(
    gtwinId: string,
    patches: ReadonlyArray<SceneItemPatch>,
  ): Promise<ReadonlyArray<SceneItem>>;
  reorder(gtwinId: string, layerOrder: ReadonlyArray<string>): Promise<void>;
  copyFrom(
    targetGtwinId: string,
    input: SceneItemsCopyFromInput,
  ): Promise<SceneItemsCopyFromResult>;
}

export interface ScenePresetsAdapter {
  activate(gtwinId: string, presetId: string): Promise<void>;
  upsert(gtwinId: string, preset: ScenePresetInput): Promise<ScenePresetInput>;
  remove(gtwinId: string, presetId: string): Promise<void>;
}

export interface PackageExportResult {
  url: string;
  bytes: number;
  sha256?: string;
  checksumVerified?: boolean;
  blob?: Blob;
}

export interface PackageAdapter {
  exportGtwin(
    gtwinId: string,
    options?: {
      mode?: "thin" | "fat";
      onProgress?: (received: number, total: number | null) => void;
    },
  ): Promise<PackageExportResult>;
}
